import React from 'react';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';

const HorseboxInfo = () => {
  const navigate = useNavigate();

  const handleBookingClick = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm py-4">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-[#2d5f3f] text-[#2d5f3f] hover:bg-[#2d5f3f] hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <img
              src="https://static.wixstatic.com/media/5924bc_1d4a840f9cb44afcb7ee8f647411679d~mv2.png/v1/fill/w_212,h_93,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/5924bc_1d4a840f9cb44afcb7ee8f647411679d~mv2.png"
              alt="Equi-Go Logo"
              className="h-12 w-auto"
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 lg:px-8 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 font-['Playfair_Display']">
            Our Horsebox
          </h1>
          <h2 className="text-2xl text-[#2d5f3f] font-semibold mb-8">
            Renault Master 3.5-ton Stallion Stall
          </h2>
          <p className="text-lg text-gray-600 mb-12">
            Built to transporter specification • Full Defra Approved
          </p>

          {/* Horse Area */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="w-8 h-8 rounded-full bg-[#2d5f3f] text-white flex items-center justify-center mr-3 text-sm">1</span>
              Horse Area
            </h3>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <p className="text-lg font-semibold text-[#2d5f3f] mb-6">
                Stalled for two with a payload of 1,050kg
              </p>
              <ul className="grid md:grid-cols-2 gap-4">
                {[
                  'Internal head height 7ft9 (will carry 17.2hh comfortably)',
                  'Fully adjustable full height stallion stall',
                  'Full height extra thick EVA kick boards',
                  'Nylon/rubber composition sealed floor',
                  'Extra thick rubber on ramp',
                  'Two fans',
                  'Tinted (bonded) windows with safety bars',
                  'LED lighting, LED ramp light, blue running lights (calming lights)',
                  '4 internal tie rings',
                  '2 external tie rings',
                  'Adjustable hay tie rings',
                  'CCTV to view horse throughout the journey',
                  'Double doors from horse area to grooms area',
                  'Lightweight - Barn Door style ramp'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2d5f3f] flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Grooms Area */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="w-8 h-8 rounded-full bg-[#2d5f3f] text-white flex items-center justify-center mr-3 text-sm">2</span>
              Grooms Area
            </h3>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <ul className="grid md:grid-cols-2 gap-4">
                {[
                  'Double doors from grooms area - This allows you to tend to each horse individually and safely',
                  'Tinted (bonded) windows with safety bars',
                  'Two roof vents for maximum ventilation',
                  '2 x saddle rack and bridle hooks',
                  'Two cushioned seats with storage under',
                  'Hanging rail and hooks',
                  'Oversized rear door - can be used as emergency exit',
                  'LED Lights'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2d5f3f] flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Cab */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="w-8 h-8 rounded-full bg-[#2d5f3f] text-white flex items-center justify-center mr-3 text-sm">3</span>
              Cab
            </h3>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <ul className="grid md:grid-cols-2 gap-4">
                {[
                  '6 speed Manual Gear Box',
                  'Three seats',
                  'USB port for charging',
                  'Reverse camera'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2d5f3f] flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Images */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Horsebox Images</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img
                  src="https://static.wixstatic.com/media/5924bc_148da214afbf4252918ecb719a65d375~mv2.jpg/v1/fill/w_754,h_600,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202025-12-13%20at%2014_03_edited.jpg"
                  alt="Horsebox"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img
                  src="https://static.wixstatic.com/media/5924bc_22146ae11723486ab609b782e94776a5~mv2.jpeg/v1/fill/w_357,h_543,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202025-12-13%20at%2014_03_16.jpeg"
                  alt="Horsebox Interior"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img
                  src="https://static.wixstatic.com/media/5924bc_d3ed399ef27242d8ab28d0c9038d65cb~mv2.jpeg/v1/fill/w_357,h_543,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202025-12-13%20at%2014_03_12%20(3).jpeg"
                  alt="Horsebox Detail"
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-gradient-to-br from-[#2d5f3f] to-[#1e4029] rounded-2xl p-12 text-white">
            <h3 className="text-3xl font-bold mb-4">Ready to Book?</h3>
            <p className="text-white/90 mb-6 text-lg">Contact us to check availability and discuss your requirements</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleBookingClick}
                className="inline-flex items-center justify-center px-8 py-3 bg-white text-[#2d5f3f] hover:bg-gray-100 rounded-lg transition-colors duration-300 font-semibold text-base w-full sm:w-auto h-[48px]"
              >
                Go to Booking Form
              </button>
              <a
                href="tel:07788754165"
                className="inline-flex items-center justify-center px-8 py-3 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-lg hover:bg-white/20 transition-colors duration-300 font-semibold text-base w-full sm:w-auto h-[48px]"
              >
                Call 07788 754165
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HorseboxInfo;